import { fetchWithAuth } from '../helpers/utils';
import {
  ActionEvent,
  ActionEventParams,
  HeartbeatEvent,
  HeartBeatEventParams,
  InsightsEventType,
  InternalEventType,
  NavigationEnd,
  PageLoadEvent,
  PageLoadParams,
  PageMeta,
  PageViewEvent,
  PageViewParams,
  TrackEvent,
} from './insights.models';

interface InsightsServiceConfig {
  enabledLogging?: boolean;
  trackingServiceUrl: string;
}

export class InsightsService {
  private buffer: TrackEvent[] = [];

  private readonly FLUSH_INTERVAL = 5000;

  private isInitialized = false;
  private currentPageViewId: string;
  private currentPageGroup: string;
  private currentPageName: string;

  private ROUTER_PAGE_META: Record<string, PageMeta> = {};

  private static instance: InsightsService;

  private enabledLogging = false;
  private trackingServiceUrl = '';

  public static getInstance() {
    if (!InsightsService.instance) {
      InsightsService.instance = new InsightsService();
    }

    return InsightsService.instance;
  }

  /**
   * Creates a new instance of InsightsService (non-singleton).
   * Useful when you need a separate instance with different configuration.
   * @returns A new InsightsService instance
   */
  public static createInstance(): InsightsService {
    return new InsightsService();
  }

  public addPageMeta(pageMeta: Record<string, PageMeta>): void {
    this.ROUTER_PAGE_META = { ...this.ROUTER_PAGE_META, ...pageMeta };
  }

  /**
   * Initialize InsightService by setting up a periodic timer
   * to send buffer of events to Tracking backend service. It
   * also send them when users leave the page.
   */
  init({ trackingServiceUrl, enabledLogging = false }: InsightsServiceConfig): void {
    if (this.isInitialized) {
      throw new Error('InsightsService is already initialized');
    }

    this.enabledLogging = enabledLogging;
    this.trackingServiceUrl = trackingServiceUrl;

    if (this.enabledLogging) {
      // eslint-disable-next-line no-console
      console.log('[InsightsService] Initialized with trackingServiceUrl:', trackingServiceUrl);
    }

    setInterval(() => {
      this.flushBuffer();
    }, this.FLUSH_INTERVAL);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushBuffer();
      }
    });

    document.defaultView.addEventListener('pagehide', () => {
      this.flushBuffer();
    });

    this.isInitialized = true;
  }

  /**
   * Capture page interaction events
   * @param event - describe action event details
   */
  actionEvent(event: ActionEventParams): void {
    const origin = {
      pageelement: event.pageelement,
      pagegroup: this.currentPageGroup,
      pagename: this.currentPageName,
    };

    const request: ActionEvent = {
      origin: origin,
      pageviewId: this.currentPageViewId,
      action_subtype: event.action_subtype,
      action_type: event.action_type,
      parameters: event.parameters,
      url_path: document.defaultView.location.pathname,
      unixtimestamp: new Date().valueOf(),
      type: InternalEventType.CLICK_EVENT,
    };

    if (this.isTrackEventValid(request)) {
      this.buffer.push(request);
    }
  }

  private isNavigationEnd(event: NavigationEnd | PageViewParams): event is NavigationEnd {
    return (event as NavigationEnd).url !== undefined && (event as NavigationEnd).type === 1;
  }

  /**
   * Capture when page is visited
   * @param event - describe page view event details
   */
  pageViewEvent(event: NavigationEnd | PageViewParams): void {
    this.currentPageViewId = this.getUUID();

    const pageMeta = this.isNavigationEnd(event) ? this.getPageMeta(event) : event;

    this.currentPageGroup = pageMeta?.pagegroup;
    this.currentPageName = pageMeta?.pagename;

    if (this.enabledLogging) {
      if (!this.currentPageGroup) {
        // eslint-disable-next-line no-console
        console.warn('WARNING - Missing page group');
      }

      if (!this.currentPageName) {
        // eslint-disable-next-line no-console
        console.warn('WARNING - Missing page name');
      }
    }

    const request: PageViewEvent = {
      page: {
        pagegroup: this.currentPageGroup,
        pagename: this.currentPageName,
      },
      url_path: document.defaultView.location.pathname,
      pageviewId: this.currentPageViewId,
      unixtimestamp: new Date().valueOf(),
      type: InternalEventType.PAGE_VIEW,
    };

    if (this.isTrackEventValid(request)) {
      this.buffer.push(request);
    }
  }

  /**
   * Capture when page content is loaded from backend
   * @param event - describe page load event details
   */
  pageLoadEvent(event: PageLoadParams): void {
    const request: PageLoadEvent = {
      page: {
        pagegroup: this.currentPageGroup,
        pagename: this.currentPageName,
      },
      page_contents: event.page_contents,
      pageviewId: this.currentPageViewId,
      url_path: document.defaultView.location.pathname,
      unixtimestamp: new Date().valueOf(),
      type: InsightsEventType.PAGE_LOAD,
    };

    if (this.isTrackEventValid(request)) {
      this.buffer.push(request);
    }
  }

  heartBeatEvent(event: HeartBeatEventParams): void {
    const heartBeatRequest: HeartbeatEvent = {
      page: {
        pagegroup: event.pagegroup,
        pagename: event.pagename,
      },
      course_id: event.course_id,
      module_id: event.module_id,
      span_ms: event.span_ms,
      pageviewId: this.currentPageViewId,
      url_path: document.defaultView.location.pathname,
      unixtimestamp: new Date().valueOf(),
      type: InternalEventType.HEARTBEAT,
    };

    if (this.isTrackEventValid(heartBeatRequest)) {
      this.buffer.push(heartBeatRequest);
    }
  }

  /**
   * Capture telemetry events (Core Web Vitals, errors, custom metrics)
   * @param events - Array of telemetry event wrappers to track
   */
  telemetryEvent(events: unknown[]): void {
    events.forEach((event) => {
      this.buffer.push(event as TrackEvent);
    });
  }

  /** Send all event in buffer to tracking API service */
  sendEvents(): void {
    this.flushBuffer();
  }

  private flushBuffer(): void {
    const events: TrackEvent[] = this.buffer.splice(0);

    if (events.length > 0) {
      const csrfParam = (document.querySelector('[name="csrf-param"]') as HTMLMetaElement).content;
      const csrfToken = (document.querySelector('[name="csrf-token"]') as HTMLMetaElement).content;

      const timestamp = `${new Date().valueOf()}`;

      const payload = new FormData();
      payload.append('events', JSON.stringify(events));
      payload.append('local_timestamp', timestamp);
      payload.append(csrfParam, csrfToken);

      const url = this.trackingServiceUrl; // '/data-insight/events';

      if (this.enabledLogging) {
        this.logEvents(events, timestamp);
      }

      const authToken = localStorage.getItem('authToken');

      // navigator.sendBeacon does not support custom headers, so we must use fetch if an authToken is present.
      if (navigator.sendBeacon && !authToken) {
        navigator.sendBeacon(url, payload);
      } else {
        fetchWithAuth(url, {
          method: 'post',
          keepalive: true,
          body: payload,
        });
      }
    }
  }

  private logEvents(events: TrackEvent[], timestamp: string): void {
    // eslint-disable-next-line no-console
    console.groupCollapsed(`Tracking events (${timestamp}})`);
    // eslint-disable-next-line no-console
    console.table(
      events.map((e) => {
        // Handle telemetry event types
        if (
          e.type === InternalEventType.WEB_VITAL_LCP ||
          e.type === InternalEventType.WEB_VITAL_FCP ||
          e.type === InternalEventType.WEB_VITAL_CLS ||
          e.type === InternalEventType.WEB_VITAL_INP ||
          e.type === InternalEventType.WEB_VITAL_TTFB ||
          e.type === InternalEventType.ERROR ||
          e.type === InternalEventType.CUSTOM ||
          e.type === InternalEventType.PAGE_LOAD ||
          e.type === InternalEventType.REPORTING ||
          e.type === InternalEventType.USER_CONTEXT
        ) {
          return {
            type: e.type,
            pageviewId: e.pageviewId,
            url_path: e.url_path,
            timestamp: 'timestamp' in e ? e.timestamp : undefined,
            raw: e,
          };
        }
        // Handle standard event types
        return {
          type: e.type,
          pagegroup: 'page' in e ? e.page.pagegroup : 'origin' in e ? e.origin?.pagegroup : undefined,
          pagename: 'page' in e ? e.page.pagename : 'origin' in e ? e.origin?.pagename : undefined,
          pageelement: 'origin' in e && e.origin.pagename,
          pageviewId: e.pageviewId,
          url_path: e.url_path,
          unixtimestamp: e.unixtimestamp,
          raw: e,
        };
      })
    );
    // eslint-disable-next-line no-console
    console.groupEnd();
  }

  private getUUID(): string {
    return crypto.randomUUID().replace(/-/g, '');
  }

  private getPageMeta(routerEvent: NavigationEnd): PageMeta {
    const pageKey = Object.keys(this.ROUTER_PAGE_META).find((key) => new RegExp(key).test(routerEvent.url));
    return this.ROUTER_PAGE_META[`${pageKey}`];
  }

  private isTrackEventValid(trackEvent: TrackEvent): boolean {
    if ('page' in trackEvent) {
      return trackEvent.page?.pagegroup?.length > 0 && trackEvent.page?.pagename?.length > 0;
    }
    if ('origin' in trackEvent) {
      return trackEvent.origin.pagegroup.length > 0 && trackEvent.origin.pagename?.length > 0;
    }

    return false;
  }
}
